//Servicios asociados a los viajes
import { amplifyClient, getEndpointV2 } from 'api';
import { getCreateStepValidationsUrl, getInfoUrl } from 'features/trips/endpoints';
import { createApiSlice } from 'helpers/createApiSlice';
import { RequestedVehicularConfigs } from 'modal/trip-create/unit-carrier-section/index';
import { call } from 'redux-saga/effects';
import { AnyRecord, Params, TripInfo, TripLocation, VehicleConfig } from 'types';

interface Data {
  folio: string;
  locations: TripLocation;
  invoice: string;
  addressee: string;
  issuer: string;
  addresseeName: string;
  travelStatus: AnyRecord;
  metadata?: {
    useCFDI?: string;
    fixTaxes?: boolean;
  };
}
type Payload = {
  tripId: string;
  tokenUser?: string;
};

export const getTrip = createApiSlice<Data, { id: string }>({
  name: 'getTrip',
  *tryGenerator(_, { payload: { id } }) {
    const response: Data = yield call(amplifyClient, {
      method: 'get',
      url: getEndpointV2({ path: '/tripmanage/trips/:id', urlParams: { id } }),
    });

    return response;
  },
});

export const updateTrip = createApiSlice<Data, { id: string; data: AnyRecord }>({
  name: 'updateTrip',
  *tryGenerator(_, { payload: { id, data } }) {
    yield call(amplifyClient, {
      method: 'put',
      url: getEndpointV2({ path: '/tripmanage/trips/:id', urlParams: { id } }),
      data,
    });

    return null;
  },
});

export const saveUnitTrailer = createApiSlice<Data, { id: string; data: AnyRecord }>({
  name: 'saveUnitTrailer',
  *tryGenerator(_, { payload: { id, data } }) {
    yield call(amplifyClient, {
      method: 'put',
      url: getInfoUrl(id, 'requestedVehicles'),
      data,
    });

    return null;
  },
});

export const completeTrip = createApiSlice<Data, { id: string }>({
  name: 'completeTrip',
  *tryGenerator(_, { payload: { id } }) {
    yield call(amplifyClient, {
      method: 'post',
      url: getEndpointV2({ path: '/tripmanage/trips/:id/complete', urlParams: { id } }),
    });

    return null;
  },
});

export const getStatusTrip = createApiSlice<unknown, Payload>({
  name: 'getStatusTrip',
  *tryGenerator(_, { payload: { tripId, tokenUser } }) {
    const statusTrip: unknown = yield call(amplifyClient, {
      method: 'post',
      url: getEndpointV2({ path: '/tracking/trip/:tripId/locations', urlParams: { tripId } }),
      data: { requestToken: tokenUser },
    });

    return statusTrip;
  },
});

export const getInfoLocation = createApiSlice<unknown, Payload>({
  name: 'getInfoLocation',
  *tryGenerator(_, { payload: { tripId } }) {
    const infoLocation: unknown = yield call(amplifyClient, {
      method: 'get',
      url: getEndpointV2({ path: '/tracking/trip/:tripId/locations', urlParams: { tripId } }),
    });

    return infoLocation;
  },
});

/**
 * GetTripSummary Slice
 */

type GetTripSummaryData = Array<{
  primaryLabel?: string;
  secondaryLabel?: string;
  details?: string;
}>;

export const getTripSummary = createApiSlice<GetTripSummaryData, { id: string }>({
  name: 'getTripSummary',
  *tryGenerator(_, { payload: { id } }) {
    const response: GetTripSummaryData = yield call(amplifyClient, {
      method: 'get',
      url: getEndpointV2({ path: '/tripmanage/trips/:id/summary', urlParams: { id } }),
    });
    return response;
  },
});

/**
 * GetTripPrice Slice
 */

export const getTripPrice = createApiSlice<{ cost: number; data: string }, { id: string }>({
  name: 'getTripPrice',
  *tryGenerator(_, { payload: { id } }) {
    const response: GetTripSummaryData = yield call(amplifyClient, {
      method: 'get',
      url: getEndpointV2({ path: '/tripmanage/trips/:id/price', urlParams: { id } }),
    });

    return response;
  },
});

/**
 * GetTripPrice Slice
 */

export const updateTripPrice = createApiSlice<unknown, { id: string; cost: number }>({
  name: 'updateTripPrice',
  *tryGenerator(_, { payload: { id, cost } }) {
    yield call(amplifyClient, {
      method: 'put',
      url: getEndpointV2({ path: '/tripmanage/trips/:id/price', urlParams: { id } }),
      data: { cost },
    });
  },
});

/**
 * tripAddUpdateNextStep
 */

export const tripAddUpdateNextStep = createApiSlice<
  unknown,
  { info: TripInfo; id: string; additionalParams?: Params }
>({
  name: 'tripAddUpdateNextStep',
  *tryGenerator(_, { payload: { info, id, additionalParams } }) {
    yield call(amplifyClient, {
      method: 'get',
      url: getCreateStepValidationsUrl(id, info, additionalParams),
    });
  },
});

/**
 * GetTripInfo Slice
 */

interface GetTripInfoPayload {
  id: string;
  info: TripInfo;
}

export const getTripInfo = createApiSlice<AnyRecord, GetTripInfoPayload>({
  name: 'getTripInfo',
  *tryGenerator(_, { payload: { id, info } }) {
    const response: AnyRecord = yield call(amplifyClient, {
      method: 'get',
      url: getInfoUrl(id, info),
    });
    return response;
  },
});

/**
 * AddTripInfo Slice
 */

interface AddTripInfoPayload {
  id: string;
  info: TripInfo;
  data?: AnyRecord;
  params?: Params;
}

export const addTripInfo = createApiSlice<null, AddTripInfoPayload>({
  name: 'addTripInfo',
  *tryGenerator(_, { payload: { id, info, data, params } }) {
    yield call(amplifyClient, {
      method: 'post',
      url: getInfoUrl(id, info, params),
      data,
    });
  },
});

/**
 * UpdateTripInfo Slice
 */

interface UpdateTripInfoPayload {
  id: string;
  info: TripInfo;
  data?: AnyRecord;
  params?: Params;
}

export const updateTripInfo = createApiSlice<AnyRecord, UpdateTripInfoPayload>({
  name: 'updateTripInfo',
  *tryGenerator(_, { payload: { id, info, data, params } }) {
    const response: AnyRecord = yield call(amplifyClient, {
      method: 'put',
      url: getInfoUrl(id, info, params),
      data,
    });

    return response;
  },
});

/**
 * RemoveTripInfo Slice
 */

interface RemoveTripInfoPayload {
  id: string;
  info: TripInfo;
  data?: AnyRecord;
  params?: AnyRecord;
}

export const removeTripInfo = createApiSlice<unknown, RemoveTripInfoPayload>({
  name: 'removeTripInfo',
  *tryGenerator(_, { payload: { id, info, data, params } }) {
    yield call(amplifyClient, {
      method: 'del',
      url: getInfoUrl(id, info, params),
      data,
    });
  },
});

/**
 * tripAddUpdateNextStep
 */

export const tripRemove = createApiSlice<unknown, { id: string }>({
  name: 'tripRemove',
  *tryGenerator(_, { payload: { id } }) {
    yield call(amplifyClient, {
      method: 'del',
      url: getEndpointV2({ path: '/tripmanage/trips/:id', urlParams: { id } }),
    });
  },
});

export const cancelTrip = createApiSlice<unknown, { tripId: string; reason: string }>({
  name: 'cancelTrip',
  *tryGenerator(_, { payload: { tripId, reason } }) {
    yield call(amplifyClient, {
      method: 'del',
      url: getEndpointV2({ path: '/tripingest/trip/cancel' }),
      data: { tripId, reason },
    });
  },
});

export const emitDocumentTrip = createApiSlice<Data, { id: string }>({
  name: 'emitDocumentTrip',
  *tryGenerator(_, { payload: { id } }) {
    yield call(amplifyClient, {
      method: 'post',
      url: getEndpointV2({ path: '/tripingest/trip/emit_document' }),
      data: { tripId: id },
    });

    return null;
  },
});

export const getVehicularConfiguration = createApiSlice<
  Array<VehicleConfig>,
  { id: string; bodyType: string }
>({
  name: 'getVehicularConfiguration',
  *tryGenerator(_, { payload: { id, bodyType } }) {
    const { configs } = yield call(amplifyClient, {
      method: 'get',
      url: getEndpointV2({
        path: '/tripmanage/trip/:id/vehicular_configs',
        urlParams: { id },
        params: { bodyType },
      }),
    });

    return configs;
  },
});

interface GetRequestedVehicularConfigsData {
  bodyDescr: string;
  bodyType: string;
  dateFrom: string;
  dateTo: string;
  configs: Array<{
    code: string;
    codeDescr: string;
  }>;
}
export const getRequestedVehicularConfigs = createApiSlice<
  GetRequestedVehicularConfigsData,
  { id: string }
>({
  name: 'getRequestedVehicularConfigs',
  *tryGenerator(_, { payload: { id } }) {
    const vehicularConfigs: RequestedVehicularConfigs = yield call(amplifyClient, {
      method: 'get',
      url: getEndpointV2({
        path: '/tripmanage/trip/:id/vehicular_configs/available',
        urlParams: { id },
      }),
    });
    return vehicularConfigs;
  },
});

export interface GetVehiclesPayload {
  idUser: string;
  id: string;
  configs?: Array<string>;
  bodyType?: string;
  date?: {
    dateFrom: string;
    dateTo: string;
  };
  prefix?: string;
  previous?: {
    config?: string;
    deep?: number;
    plates?: Array<string>;
  };
}

export const interested = createApiSlice<unknown, { id: string; cost: number }>({
  name: 'interested',
  *tryGenerator(_, { payload: { id, cost } }) {
    yield call(amplifyClient, {
      method: 'post',
      url: getEndpointV2({ path: '/tripmanage/trips/:id/bids', urlParams: { id } }),
      data: { price: cost },
    });
  },
});

export const notInterested = createApiSlice<unknown, { id: string; idBid: string }>({
  name: 'notInterested',
  *tryGenerator(_, { payload: { id, idBid } }) {
    yield call(amplifyClient, {
      method: 'del',
      url: getEndpointV2({
        path: '/tripmanage/trips/:id/bids/:idBid',
        urlParams: { id, idBid },
      }),
    });
  },
});

export const assignCarrier = createApiSlice<unknown, { id: string; idBid: string }>({
  name: 'assignCarrier',
  *tryGenerator(_, { payload: { id, idBid } }) {
    yield call(amplifyClient, {
      method: 'put',
      url: getEndpointV2({ path: '/tripmanage/trips/:id/bids/:idBid', urlParams: { id, idBid } }),
    });
  },
});

export const getVehicle = createApiSlice<unknown, GetVehiclesPayload>({
  name: 'getVehicle',
  *tryGenerator(_, { payload: { idUser, id, configs, bodyType, date, prefix, previous } }) {
    const vehicles: unknown = yield call(amplifyClient, {
      method: 'post',
      url: getEndpointV2({
        path: '/auth/user/:idUser/trip/:id/vehicles/available',
        urlParams: { idUser, id },
      }),
      data: {
        configs,
        bodyType,
        date,
        prefix,
        previous,
      },
    });

    return vehicles;
  },
});
